import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, Lock, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { toast } from '@/components/ui/sonner';

const MIN_PASSWORD_LENGTH = 6;

/**
 * Translate raw Supabase password-update errors into clear, friendly copy
 * for users who just clicked a reset email link.
 */
const friendlyResetError = (raw: string): string => {
  const lower = (raw || '').toLowerCase();
  if (lower.includes('expired') || lower.includes('invalid token') || lower.includes('jwt')) {
    return 'This reset link has expired. Please request a new password reset email.';
  }
  if (lower.includes('weak') || lower.includes('password should')) {
    return `Password is too weak. Use at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (lower.includes('same') && lower.includes('password')) {
    return 'New password must be different from your current password.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Network error. Check your connection and try again.';
  }
  return raw || 'Failed to update password. Please try again.';
};

/**
 * /reset-password
 *
 * Users land here after clicking the secure link in their password-reset
 * email. The Supabase auth client (with detectSessionInUrl: true, default)
 * parses the recovery token from the URL fragment and establishes a
 * temporary recovery session, so we can call `auth.updateUser({ password })`
 * to set the new password.
 *
 * Flow:
 *   1. Parse the recovery token (PASSWORD_RECOVERY auth event).
 *   2. Show new password + confirm password fields with validation.
 *   3. On success → "Password updated successfully" + sign user out and
 *      send them home so they can sign in with the new password.
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { updatePassword } = useAuth();
  const [hasRecoverySession, setHasRecoverySession] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let mounted = true;

    // Listen for the PASSWORD_RECOVERY auth event Supabase fires after it
    // parses the recovery token out of the URL.
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        if (mounted) setHasRecoverySession(true);
      }
    });

    // Also check the current session in case the event already fired before
    // this component mounted (or the user was already signed in).
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (mounted && data.session) setHasRecoverySession(true);
        else if (mounted) {
          // give the SDK a beat to parse the URL hash
          setTimeout(async () => {
            const { data: d2 } = await supabase.auth.getSession();
            if (mounted) setHasRecoverySession(!!d2.session);
          }, 800);
        }
      } catch {
        if (mounted) setHasRecoverySession(false);
      }
    })();

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Live validation hints for the two password fields.
  const passwordTooShort = useMemo(
    () => password.length > 0 && password.length < MIN_PASSWORD_LENGTH,
    [password]
  );
  const passwordsMismatch = useMemo(
    () => confirm.length > 0 && password !== confirm,
    [password, confirm]
  );
  const passwordsMatch = useMemo(
    () => confirm.length > 0 && password.length >= MIN_PASSWORD_LENGTH && password === confirm,
    [password, confirm]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match. Please re-enter to confirm.");
      return;
    }

    setLoading(true);
    const result = await updatePassword(password);
    setLoading(false);

    if (!result.ok) {
      setError(friendlyResetError(result.error));
      return;
    }

    setSuccess(true);
    toast.success('Password updated successfully');

    // Per spec: redirect to login page so the user can sign in with the new
    // password. We sign them out first so the home page presents a clean
    // sign-in entry point (Supabase's updateUser keeps the recovery session
    // alive otherwise, which would feel like an invisible login).
    setTimeout(async () => {
      try {
        await supabase.auth.signOut();
      } catch {
        /* non-fatal — we still want to navigate home */
      }
      navigate('/login', { replace: true });
    }, 1600);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-fuchsia-50 via-pink-50 to-rose-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-br from-fuchsia-700 via-fuchsia-600 to-pink-600 text-white p-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center ring-2 ring-white/30">
              <img
                src="/brand/kinscircle-tree-icon.png"
                alt="KinsCircle"
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            <div>
              <h1 className="font-serif text-2xl font-bold leading-tight">Reset your password</h1>
              <p className="text-white/80 text-xs mt-0.5">
                Choose a new password to access your KinsCircle.
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {hasRecoverySession === null && (
            <div className="flex items-center justify-center py-10 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying reset link…
            </div>
          )}

          {hasRecoverySession === false && (
            <div className="space-y-4 text-center">
              <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg p-3 text-left">
                This reset link is invalid or has expired. Please request a new password reset email
                from the sign-in screen.
              </div>
              <Button
                onClick={() => navigate('/login', { replace: true })}
                className="w-full rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
              >
                Back to sign in
              </Button>
            </div>
          )}

          {hasRecoverySession && !success && (
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <Label htmlFor="new-password" className="text-slate-800">New Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="new-password"
                    type="password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
                    className="pl-9"
                    autoComplete="new-password"
                    aria-invalid={passwordTooShort}
                  />
                </div>
                {passwordTooShort && (
                  <p className="text-xs text-amber-700 mt-1">
                    Password must be at least {MIN_PASSWORD_LENGTH} characters.
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="confirm-password" className="text-slate-800">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirm-password"
                    type="password"
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Re-enter your new password"
                    className="pl-9"
                    autoComplete="new-password"
                    aria-invalid={passwordsMismatch}
                  />
                </div>
                {passwordsMismatch ? (
                  <p className="text-xs text-red-600 mt-1">Passwords don't match.</p>
                ) : passwordsMatch ? (
                  <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Passwords match.
                  </p>
                ) : null}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading || passwordTooShort || passwordsMismatch || !password || !confirm}
                className="w-full rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…
                  </>
                ) : (
                  'Reset Password'
                )}
              </Button>

              <p className="text-center text-xs text-slate-500">
                After resetting, you'll be returned to the sign-in screen to log in with your new
                password.
              </p>
            </form>
          )}

          {success && (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <p className="text-slate-800 font-semibold">Password updated successfully</p>
              <p className="text-slate-500 text-sm">Redirecting you to the sign-in page…</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
